import { useCallback, useMemo, useState } from 'react';

import { DatePicker } from 'react-datepicker';
import { PatternFormat } from 'react-number-format';
import { twMerge } from 'tailwind-merge';

import 'react-datepicker/dist/react-datepicker.css';

import { dateTime } from '@/_shared';

import { Input } from './input';
import type { InputProps } from './type';

export type DateInputProps = Omit<InputProps, 'value'> & {
  value?: string | null;
  onValueChange?: (value: string | null) => void;
  minDate?: Date;
  maxDate?: Date;
  popperPlacement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  customInput?: React.ReactElement;
  renderInPortal?: boolean;
  preventOpenOnFocus?: boolean;
};

export function DateInput({
  label,
  value,
  minDate,
  disabled,
  maxDate,
  onValueChange,
  popperPlacement,
  customInput,
  renderInPortal = true,
  className,
  containerProps,
  autoFocus,
  preSlot,
  preventOpenOnFocus,
}: DateInputProps) {
  const [hasInputError, setHasInputError] = useState(false);

  const oDateValue = useMemo(() => {
    try {
      return value ? dateTime.parse(value).startOf('day').toDate() : null;
    } catch (error) {
      console.error('Invalid date format:', value, error);
      return null;
    }
  }, [value]);

  const handleChange = useCallback(
    (date: Date | null) => {
      if (!date) {
        onValueChange?.(null);
        setHasInputError(false);
      } else {
        const normalized = dateTime.parse(date).startOf('day');
        onValueChange?.(dateTime.format(normalized, 'YYYY-MM-DD'));
      }
    },
    [onValueChange]
  );

  const handleClear = useCallback(() => {
    handleChange(null);
    setHasInputError(false);
  }, [handleChange]);

  const patternInput = useMemo(
    () => (
      <PatternFormat
        mask="#"
        format="##/##/####"
        customInput={(props) => (
          <Input
            {...props}
            preSlot={preSlot}
            containerProps={containerProps}
            onChange={(e) => {
              if (!e.target.value) {
                handleClear();
              }
              props?.onChange?.(e);
            }}
            label={label}
          />
        )}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <DatePicker
      minDate={minDate}
      maxDate={maxDate}
      selected={oDateValue}
      showMonthDropdown
      showYearDropdown
      onChange={handleChange}
      disabled={disabled}
      autoFocus={autoFocus}
      popperPlacement={popperPlacement}
      portalId={renderInPortal ? 'root-portal' : undefined}
      dateFormat="MM/dd/yyyy"
      className={twMerge(className, hasInputError ? 'border-error' : '')}
      customInput={customInput || patternInput}
      preventOpenOnFocus={preventOpenOnFocus}
    />
  );
}
